// No direct PHP counterpart.
// Shared JS controller helper for Monty owner endpoints.
// Consolidates repeated controller plumbing from:
// - api/fuel/app/classes/controller/api/monty/users/*.php
// - api/fuel/app/classes/controller/api/monty/groups/*.php
// - api/fuel/app/classes/controller/api/monty/buddies/*.php
// Role in the port:
// - keep endpoint files 1:1 with PHP route structure
// - centralize owner resolution / permissions / filter bootstrapping in one JS-only layer
// - avoid duplicating offline-only wiring across every thin controller
import F from 'offline/filter/constants'
import DB from 'offline/db'
import Util from 'offline/util'
import User from 'offline/model/user'
import Group from 'offline/model/group'
import Buddies from 'offline/model/buddies'
import Config from 'offline/model/config'

function ownerModel(ownerType) {
	if (ownerType === F.OWNER_USER) return User
	if (ownerType === F.OWNER_GROUP) return Group
	if (ownerType === F.OWNER_BUDDIES) return Buddies
	return null
}

function ownerUsers(owner, ownerType) {
	if (ownerType === F.OWNER_GROUP) {
		return Util.pluck(DB.query('SELECT user_id FROM link_group_user WHERE group_id = ?', [owner.get_id()]), 'user_id')
	}
	if (ownerType === F.OWNER_BUDDIES) {
		return Util.pluck(DB.query('SELECT user_id FROM link_buddies_user WHERE buddies_id = ?', [owner.get_id()]), 'user_id')
	}
	return []
}

function defaultPermissions(ownerType) {
	if (ownerType === F.OWNER_USER) {
		return {
			is: { owner: false },
			user: { sync: false }
		}
	}

	if (ownerType === F.OWNER_GROUP) {
		return {
			group: { add: false, edit: false, remove: false, delete: false, join: false, leave: false }
		}
	}

	return {}
}

export function resolveOwner(params, ownerType) {
	var id = params.id
	var slug = params.slug
	var rows = []
	var Model = ownerModel(ownerType)

	if (id) {
		if (ownerType === F.OWNER_USER) rows = DB.query('SELECT * FROM user WHERE user_id = ?', [id])
		if (ownerType === F.OWNER_GROUP) rows = DB.query('SELECT * FROM groups WHERE group_id = ?', [id])
		if (ownerType === F.OWNER_BUDDIES) rows = DB.query('SELECT * FROM buddies WHERE buddies_id = ?', [id])
	} else if (slug) {
		if (ownerType === F.OWNER_USER) rows = DB.query('SELECT * FROM user WHERE user_name = ?', [slug])
		if (ownerType === F.OWNER_GROUP) rows = DB.query('SELECT * FROM groups WHERE group_slug = ?', [slug])
		if (ownerType === F.OWNER_BUDDIES) {
			var userRows = DB.query('SELECT user_id FROM user WHERE user_name = ?', [slug])
			if (userRows.length) rows = DB.query('SELECT * FROM buddies WHERE user_id = ?', [userRows[0].user_id])
		}
	}

	return rows.length && Model ? Model.forge(rows[0]) : null
}

export function resolveUsers(owner, ownerType, filter) {
	if (ownerType === F.OWNER_USER) return [owner.get_id()]

	if (ownerType === F.OWNER_GROUP) {
		var groupUsers = ownerUsers(owner, ownerType)
		if (filter && filter.users && filter.users.length) {
			groupUsers = groupUsers.filter(function(userId) { return filter.users.indexOf(userId) !== -1 })
		}
		return groupUsers
	}

	if (ownerType === F.OWNER_BUDDIES) {
		var buddyUsers = ownerUsers(owner, ownerType)
		if (filter && filter.users && filter.users.length) {
			buddyUsers = buddyUsers.filter(function(userId) { return filter.users.indexOf(userId) !== -1 })
		}
		return buddyUsers
	}

	return []
}

export function buildFilterConfig(params, ownerType, overrides) {
	var owner = resolveOwner(params, ownerType)
	if (!owner) return null
	var sessionUserId = params.sessionUserId || null
	var sessionSettings = Config.user(sessionUserId || false)
	var ownerSettings = ownerType === F.OWNER_USER ? Config.user(owner.get_id()) : {}
	var canViewInventoryLocation = !!(
		ownerType === F.OWNER_USER &&
		ownerSettings &&
		ownerSettings.collection &&
		ownerSettings.collection.inventory &&
		ownerSettings.collection.inventory.location &&
		(ownerSettings.collection.inventory.location.public || owner.get_id() === sessionUserId)
	)

	var config = {
		owner: owner,
		cache: params.key || false,
		filter: params.filter || {},
		page: params.page || 1,
		toPage: params.toPage || false,
		knownUsers: params.knownUsers || [],
		users: resolveUsers(owner, ownerType, params.filter || {}),
		userID: ownerType === F.OWNER_USER ? owner.get_id() : sessionUserId,
		sessionID: sessionUserId,
		perpage: F.PERPAGE,
		settings: {
			session: sessionSettings || {},
			owner: ownerSettings || {}
		},
		can: {
			'view.inventory.location': canViewInventoryLocation
		}
	}

	if (overrides) {
		var keys = Object.keys(overrides)
		for (var i = 0; i < keys.length; i++) config[keys[i]] = overrides[keys[i]]
	}

	return config
}

export function buildOwnerVerifyData(owner, ownerType) {
	var data = owner.to_array()
	if (ownerType === F.OWNER_GROUP || ownerType === F.OWNER_BUDDIES) {
		data.users = ownerUsers(owner, ownerType)
	}
	return data
}

export function buildOwnerPermissions(owner, ownerType, sessionUserId) {
	var permissions = defaultPermissions(ownerType)
	if (!sessionUserId) return permissions

	if (ownerType === F.OWNER_USER) {
		permissions.is.owner = owner.get_id() === sessionUserId
		permissions.user.sync = owner.get_id() === sessionUserId
		return permissions
	}

	if (ownerType === F.OWNER_GROUP) {
		if (owner.get_user_id() === sessionUserId) {
			permissions.is = { owner: true, member: true }
			permissions.is.owner = true
			permissions.is.member = true
			permissions.group.add = true
			permissions.group.edit = true
			permissions.group.remove = true
			permissions.group.delete = true
			return permissions
		}

		var isMember = DB.scalar('SELECT COUNT(*) FROM link_group_user WHERE group_id = ? AND user_id = ?', [owner.get_id(), sessionUserId]) > 0
		if (isMember) {
			permissions.is = { member: true }
			permissions.is.member = true
			permissions.group.leave = true
		} else {
			permissions.group.join = owner.get_membership() === 1
		}

		return permissions
	}

	if (ownerType === F.OWNER_BUDDIES) {
		if (owner.get_user_id() === sessionUserId) permissions.is = { owner: true }
		return permissions
	}

	return permissions
}

export function runOwnerEndpoint(params, ownerType, options) {
	var owner = resolveOwner(params, ownerType)
	if (!owner) {
		return Promise.reject({ error: 'Owner not found in offline database', offline: true })
	}

	var data = buildOwnerVerifyData(owner, ownerType)
	var permissions = options && options.permissions === false
		? undefined
		: buildOwnerPermissions(owner, ownerType, params.sessionUserId || null)

	var response = { status: 1, session: { offline: true }, data: data }
	if (permissions !== undefined) response.permissions = permissions
	return Promise.resolve(response)
}

export function runFilterEndpoint(FilterClass, params, ownerType, overrides) {
	var config = buildFilterConfig(params, ownerType, overrides)

	if (!config) {
		return Promise.reject({ error: 'Owner not found in offline database', offline: true })
	}

	var permissions = buildOwnerPermissions(config.owner, ownerType, params.sessionUserId || null)

	var response = FilterClass
		.forge(config)
		.run()
		.to_array({
			status: 1,
			session: { offline: true },
			permissions: permissions
		})

	return Promise.resolve(response)
}

export default {
	resolveOwner: resolveOwner,
	resolveUsers: resolveUsers,
	buildFilterConfig: buildFilterConfig,
	buildOwnerVerifyData: buildOwnerVerifyData,
	buildOwnerPermissions: buildOwnerPermissions,
	runOwnerEndpoint: runOwnerEndpoint,
	runFilterEndpoint: runFilterEndpoint
}
